import React, { useState, useEffect, useMemo } from 'react';
import { Reorder, AnimatePresence, motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronDown,
    faChevronUp,
    faHome,
    faCar,
    faGasPump,
    faUtensils,
    faShoppingCart,
    faBolt,
    faWater,
    faWifi,
    faPhone,
    faHeartbeat,
    faStethoscope,
    faPills,
    faGraduationCap,
    faChild,
    faBaby,
    faShieldAlt,
    faCreditCard,
    faDumbbell,
    faTshirt,
    faCut,
    faGamepad,
    faFilm,
    faMusic,
    faBook,
    faNewspaper,
    faGift,
    faDollarSign,
    faBriefcase,
    faTags,
} from '@/icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { ExpenseItem, ExpenseItemData } from './ExpenseItem';
import { CustomAddButton } from './CustomAddButton';
import { ExpenseGroupModel, ExpenseModel } from '../types/case';
import { ensureFinancialLineId, newFinancialLineId } from '../utils/financialLineId';

export interface ExpenseGroupProps {
    groupName: string;
    expenses: ExpenseModel[];
    groupIndex: number;
    onUpdate: (groupIndex: number, expenses: ExpenseModel[]) => void;
    isReadOnly?: boolean;
    forceCollapsed?: boolean;
}

/**
 * Infer an appropriate icon for an expense group name using fuzzy matching
 */
const getExpenseGroupIcon = (groupName: string): IconDefinition => {
    const normalizedName = groupName.toLowerCase().trim();

    // Define keyword mappings to icons
    const iconMappings: Array<{ keywords: string[]; icon: IconDefinition }> = [
        // Housing
        { keywords: ['housing', 'rent', 'mortgage', 'home', 'residence', 'dwelling', 'apartment', 'condo'], icon: faHome },

        // Transportation
        { keywords: ['transport', 'vehicle', 'car', 'auto', 'automobile', 'gas', 'fuel', 'gasoline'], icon: faCar },
        { keywords: ['gas', 'fuel', 'gasoline', 'petrol'], icon: faGasPump },

        // Food
        { keywords: ['food', 'groceries', 'grocery', 'dining', 'restaurant', 'meal', 'eating'], icon: faUtensils },
        { keywords: ['shopping', 'retail', 'store', 'merchandise'], icon: faShoppingCart },

        // Utilities
        { keywords: ['electric', 'electricity', 'power', 'energy'], icon: faBolt },
        { keywords: ['water', 'sewer', 'sewage'], icon: faWater },
        { keywords: ['internet', 'wifi', 'broadband', 'cable', 'tv', 'television'], icon: faWifi },
        { keywords: ['phone', 'telephone', 'mobile', 'cellular'], icon: faPhone },

        // Healthcare
        { keywords: ['health', 'medical', 'doctor', 'physician', 'hospital'], icon: faStethoscope },
        { keywords: ['pharmacy', 'prescription', 'medication', 'drug'], icon: faPills },
        { keywords: ['insurance', 'health insurance'], icon: faHeartbeat },

        // Education
        { keywords: ['education', 'school', 'tuition', 'college', 'university', 'student'], icon: faGraduationCap },

        // Childcare
        { keywords: ['childcare', 'child care', 'daycare', 'day care', 'babysitter', 'nanny'], icon: faBaby },
        { keywords: ['child', 'children', 'kids'], icon: faChild },

        // Insurance (general)
        { keywords: ['insurance'], icon: faShieldAlt },

        // Debt/Payments
        { keywords: ['debt', 'credit', 'payment', 'loan', 'finance'], icon: faCreditCard },

        // Personal Care
        { keywords: ['gym', 'fitness', 'exercise', 'workout'], icon: faDumbbell },
        { keywords: ['clothing', 'clothes', 'apparel'], icon: faTshirt },
        { keywords: ['hair', 'salon', 'barber', 'grooming'], icon: faCut },

        // Entertainment
        { keywords: ['entertainment', 'recreation', 'leisure'], icon: faGamepad },
        { keywords: ['movie', 'cinema', 'theater', 'streaming'], icon: faFilm },
        { keywords: ['music', 'concert', 'subscription'], icon: faMusic },

        // Reading/Misc
        { keywords: ['book', 'reading', 'magazine'], icon: faBook },
        { keywords: ['newspaper', 'news', 'subscription'], icon: faNewspaper },

        // Other
        { keywords: ['gift', 'donation', 'charity'], icon: faGift },
        { keywords: ['business', 'work', 'office'], icon: faBriefcase },
        { keywords: ['misc', 'miscellaneous', 'other', 'expense'], icon: faTags },
    ];

    // Check for exact or partial matches
    for (const mapping of iconMappings) {
        for (const keyword of mapping.keywords) {
            if (normalizedName.includes(keyword)) {
                return mapping.icon;
            }
        }
    }

    // Default fallback icon
    return faDollarSign;
};

export const ExpenseGroup: React.FC<ExpenseGroupProps> = ({
    groupName,
    expenses,
    groupIndex,
    onUpdate,
    isReadOnly = false,
    forceCollapsed = false,
}) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [lastAddedIndex, setLastAddedIndex] = useState<number | null>(null);

    // Sync collapse state with forceCollapsed prop
    useEffect(() => {
        if (forceCollapsed !== undefined) {
            setIsCollapsed(forceCollapsed);
        }
    }, [forceCollapsed]);

    // Clear lastAddedIndex after focusing
    useEffect(() => {
        if (lastAddedIndex !== null) {
            const timer = setTimeout(() => {
                setLastAddedIndex(null);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [lastAddedIndex]);

    // Generate stable keys for expense items
    const getStableKey = (expense: ExpenseModel, index: number): string => {
        return expense.lineId || `expense-${groupIndex}-${index}`;
    };

    // Convert ExpenseModel to ExpenseItemData
    const expenseModelToItemData = (expense: ExpenseModel, index: number): ExpenseItemData => {
        return {
            id: getStableKey(expense, index),
            name: expense.name || '',
            amount: expense.amount || 0,
            lineId: expense.lineId,
        };
    };

    // Convert ExpenseItemData back to ExpenseModel
    const itemDataToExpenseModel = (itemData: ExpenseItemData): ExpenseModel => {
        return {
            name: itemData.name,
            amount: itemData.amount,
            lineId: itemData.lineId,
        };
    };

    // Handle adding a new expense
    const handleAddExpense = () => {
        setIsCollapsed(false);
        const newExpense: ExpenseModel = {
            name: '',
            amount: 0,
            lineId: newFinancialLineId(),
        };
        const updatedExpenses = [...expenses, newExpense];
        const newIndex = updatedExpenses.length - 1;
        setLastAddedIndex(newIndex);
        onUpdate(groupIndex, updatedExpenses);
    };

    // Ensure all existing expenses have lineId for stable keys
    useEffect(() => {
        const needsPatch = expenses.some((exp) => !exp.lineId);
        if (needsPatch) {
            const updatedExpenses = expenses.map((exp) => ({
                ...exp,
                lineId: ensureFinancialLineId(exp.lineId),
            }));
            onUpdate(groupIndex, updatedExpenses);
        }
    }, []); // Only run once on mount

    // Handle updating an expense
    const handleUpdateExpense = (expenseIndex: number, field: keyof ExpenseItemData, value: ExpenseItemData[keyof ExpenseItemData]) => {
        const updatedExpenses = [...expenses];
        const expense = updatedExpenses[expenseIndex];

        if (!expense) return;

        // Convert to ExpenseItemData for updates
        const itemData = expenseModelToItemData(expense, expenseIndex);

        // Update the field
        (itemData as any)[field] = value;

        // Convert back to ExpenseModel and update
        updatedExpenses[expenseIndex] = itemDataToExpenseModel(itemData);

        onUpdate(groupIndex, updatedExpenses);
    };

    // Handle removing an expense
    const handleRemoveExpense = (expenseIndex: number) => {
        const updatedExpenses = expenses.filter((_, index) => index !== expenseIndex);
        onUpdate(groupIndex, updatedExpenses);
    };

    // Handle moving an expense up
    const handleMoveUp = (expenseIndex: number) => {
        if (expenseIndex === 0) return;
        const updatedExpenses = [...expenses];
        [updatedExpenses[expenseIndex - 1], updatedExpenses[expenseIndex]] =
            [updatedExpenses[expenseIndex], updatedExpenses[expenseIndex - 1]];
        onUpdate(groupIndex, updatedExpenses);
    };

    // Handle moving an expense down
    const handleMoveDown = (expenseIndex: number) => {
        if (expenseIndex === expenses.length - 1) return;
        const updatedExpenses = [...expenses];
        [updatedExpenses[expenseIndex], updatedExpenses[expenseIndex + 1]] =
            [updatedExpenses[expenseIndex + 1], updatedExpenses[expenseIndex]];
        onUpdate(groupIndex, updatedExpenses);
    };

    // Handle reordering expenses via drag and drop
    const handleReorder = (newOrder: ExpenseItemData[]) => {
        const expenseModels: ExpenseModel[] = newOrder.map(itemData => itemDataToExpenseModel(itemData));
        // Immediately update to ensure the new order is persisted
        // This prevents the revert issue by ensuring the parent state is updated synchronously
        onUpdate(groupIndex, expenseModels);
    };

    // Capitalize first letter of group name for button text
    const capitalizedGroupName = (groupName || 'Expenses').charAt(0).toUpperCase() + (groupName || 'Expenses').slice(1).toLowerCase();

    // Convert expenses to ExpenseItemData format for Reorder.Group
    // Use useMemo to ensure stable references and prevent recalculation on every render
    // Create a stable key based on expenses content and order
    const expensesKey = expenses.map((exp, idx) => exp.lineId || `exp-${idx}-${exp.name || ''}-${exp.amount || 0}`).join('|');
    const expenseItemsData: ExpenseItemData[] = useMemo(() => {
        return expenses.map((expense, index) =>
            expenseModelToItemData(expense, index)
        );
    }, [expensesKey, groupIndex]);

    // Calculate total for the expense group
    const groupTotal = useMemo(() => {
        return expenses.reduce((sum, expense) => sum + (expense.amount || 0), 0);
    }, [expenses]);

    // Format currency helper
    const formatCurrency = (amount: number) => {
        return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
        <div style={{
            backgroundColor: theme.colors.surfaceSecondary,
            borderRadius: theme.borderRadius.md,
            border: `1px solid ${theme.colors.border.default}`,
            paddingTop: theme.spacing.md,
            paddingBottom: theme.spacing.sm,
            paddingLeft: theme.spacing.md,
            paddingRight: theme.spacing.md,
            marginBottom: theme.spacing.md,
            boxShadow: theme.shadows.md,
        }}>
            {/* Group Header - Clickable to collapse/expand */}
            <div className="rw-grid-shrink rw-summary-row"
                onClick={expenses.length > 0 ? () => setIsCollapsed(!isCollapsed) : undefined}
                style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 180px auto',
                    gap: theme.spacing.md,
                    alignItems: 'center',
                    marginBottom: theme.spacing.sm,
                    cursor: expenses.length > 0 ? 'pointer' : 'default',
                }}
            >
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.sm,
                }}>
                    <FontAwesomeIcon
                        icon={getExpenseGroupIcon(groupName)}
                        style={{
                            fontSize: theme.typography.fontSize.lg,
                            color: theme.colors.primary,
                        }}
                    />
                    <h3 style={{
                        ...themedStyles.text.heading,
                        fontSize: theme.typography.fontSize.lg,
                        fontWeight: theme.typography.fontWeight.medium,
                        color: theme.colors.text.primary,
                        margin: 0,
                    }}>
                        {groupName}
                        <span style={{
                            ...themedStyles.text.body,
                            fontWeight: theme.typography.fontWeight.medium,
                            color: theme.colors.text.secondary,
                            marginLeft: theme.spacing.xs,
                            opacity: 0.9,
                        }}>
                            ({expenses.length})
                        </span>
                    </h3>
                </div>
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    width: '100%',
                    // Account for MonetaryInput internal structure to align with the number's right edge:
                    // - Input field right padding
                    // - Unit text "/ mo" width (text + left padding + right padding)
                    // - Calculator button and dollar sign spacing
                    // Adjust this value to fine-tune alignment
                    paddingRight: '160px',
                }}>
                    <span style={{
                        ...themedStyles.text.body,
                        fontWeight: theme.typography.fontWeight.medium,
                        color: theme.colors.text.primary,
                        fontSize: theme.typography.fontSize.md,
                        textAlign: 'right',
                    }}>
                        {formatCurrency(groupTotal)}
                    </span>
                </div>
                <FontAwesomeIcon
                    icon={isCollapsed ? faChevronDown : faChevronUp}
                    style={{
                        fontSize: theme.typography.fontSize.md,
                        color: theme.colors.text.secondary,
                        visibility: expenses.length === 0 ? 'hidden' : 'visible',
                    }}
                />
            </div>

            {/* Expense Items List */}
            <AnimatePresence>
                {!isCollapsed && (
                    <motion.div
                        initial={{ opacity: 0, y: -20, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: 'auto' }}
                        exit={{ opacity: 0, y: -20, height: 0 }}
                        transition={{
                            duration: 0.3,
                            ease: [0.4, 0, 0.2, 1],
                        }}
                        style={{
                            overflow: 'hidden',
                        }}
                    >
                        {expenses.length > 0 && (
                            <Reorder.Group
                                axis="y"
                                values={expenseItemsData}
                                onReorder={handleReorder}
                                style={{
                                    marginBottom: theme.spacing.sm,
                                    listStyle: 'none',
                                    padding: 0,
                                    margin: 0,
                                }}
                            >
                                {expenseItemsData.map((expenseItemData, expenseIndex) => {
                                    const stableKey = expenseItemData.lineId || expenseItemData.id || `expense-${groupIndex}-${expenseIndex}`;

                                    return (
                                        <ExpenseItem
                                            key={stableKey}
                                            expense={expenseItemData}
                                            index={expenseIndex}
                                            totalExpenses={expenseItemsData.length}
                                            onUpdate={(field, value) => handleUpdateExpense(expenseIndex, field, value)}
                                            onRemove={() => handleRemoveExpense(expenseIndex)}
                                            onMoveUp={() => handleMoveUp(expenseIndex)}
                                            onMoveDown={() => handleMoveDown(expenseIndex)}
                                            canMoveUp={expenseIndex > 0}
                                            canMoveDown={expenseIndex < expenseItemsData.length - 1}
                                            autoFocus={lastAddedIndex === expenseIndex}
                                            isReadOnly={isReadOnly}
                                            expenseIndex={expenseIndex}
                                            expenses={expenseItemsData}
                                            lastAddedIndex={lastAddedIndex}
                                            stableKey={stableKey}
                                            theme={theme}
                                        />
                                    );
                                })}
                            </Reorder.Group>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Add Button - Only visible when expanded */}
            {!isCollapsed && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    marginTop: theme.spacing.sm,
                }}>
                    <CustomAddButton
                        onClick={() => {
                            setIsCollapsed(false);
                            handleAddExpense();
                        }}
                        disabled={isReadOnly}
                    >
                        Add {capitalizedGroupName} Expense
                    </CustomAddButton>
                </div>
            )}
        </div>
    );
};

